import type { FC } from 'react';
import { lazy } from 'react';
import type { ProductCalculation } from 'src/apis/capacity-management/types';
import type { OptionalCapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useDraftedProducts } from 'src/apis/capacity-management/use-drafted-products';
import { Alert } from 'src/components/feedback/alert';
import { PageHeaderBackLink } from 'src/components/layout/page-header';
import { PageSpinner } from 'src/components/spinner-container';
import type { Referrer } from 'src/hooks/use-referrer';
import { useReferrer } from 'src/hooks/use-referrer';
import type { DraftedProductRequest } from 'src/pages/transport/products/drafted/detail/use-drafted-product';
import { useDraftedProduct } from 'src/pages/transport/products/drafted/detail/use-drafted-product';
import { useDraftedProductsDetailParams } from 'src/pages/transport/products/drafted/detail/use-page-params';
import { useProductComparisonGraph } from 'src/pages/transport/products/drafted/detail/use-product-comparison-graph';
import { useUnderlyingData } from 'src/pages/transport/products/drafted/detail/use-underlying-data';

const Page = lazy(() => import('./page'));

type LoadDataProps = {
  draftedProduct: DraftedProductRequest;
  calculation?: ProductCalculation;
  referrer: Referrer;
};

const LoadData: FC<LoadDataProps> = ({
  draftedProduct,
  calculation,
  referrer,
}) => {
  const pageParams = useDraftedProductsDetailParams({ calculation });
  const underlyingData = useUnderlyingData({
    product: draftedProduct,
    pageParams,
  });
  const productComparisonGraph = useProductComparisonGraph({
    product: draftedProduct.response?.data,
  });

  return (
    <Page
      underlyingData={underlyingData}
      draftedProduct={draftedProduct}
      pageParams={pageParams}
      referrer={referrer}
      productComparisonGraph={productComparisonGraph}
    />
  );
};

type Props = {
  capacityManagementRoot: OptionalCapacityManagementRootRequest;
};

export const ReportingUploadsDraftedDetail: FC<Props> = ({
  capacityManagementRoot,
}) => {
  const draftedProducts = useDraftedProducts({ capacityManagementRoot });
  const draftedProduct = useDraftedProduct({ draftedProducts });

  const referrer = useReferrer({
    label: 'Products',
    location: '/transport/products/drafted',
  });

  // we only handle 410 explicitely - see hook
  if (draftedProduct.error)
    return (
      <div className="grid items-center gap-md">
        <PageHeaderBackLink referrer={referrer} />
        <Alert type="warning" data-testid="outdated-drafted-product-alert">
          This drafted product is outdated.
        </Alert>
      </div>
    );

  if (!draftedProduct.response) return <PageSpinner />;
  return (
    <LoadData
      draftedProduct={draftedProduct}
      calculation={draftedProduct.response.data.calculation}
      referrer={referrer}
    />
  );
};
